import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, itemImage } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Crown, Shirt, LayoutGrid, Boxes, Library, UserRound, Camera, Images, Flame, Sparkles, ListMusic, Tag, ArrowLeftRight, Handshake, Wallet, Gem, Award, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Crown, Shirt, LayoutGrid, Boxes, Library, UserRound, Camera, Images, Flame, Sparkles, ListMusic, Tag, ArrowLeftRight, Handshake, Wallet, Gem };

export const BadgeMedal = ({ badge, size = "md" }) => {
  const Icon = ICONS[badge.icon] || Award;
  const dim = size === "lg" ? "h-14 w-14" : size === "sm" ? "h-8 w-8" : "h-11 w-11";
  const ic = size === "lg" ? "h-6 w-6" : size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5";
  return (
    <span className={`inline-flex ${dim} items-center justify-center rounded-full border ${badge.earned ? "bg-gold-soft border-gold/50 text-gold-deep" : "bg-gray-50 border-border text-gray-400"}`} data-testid={`badge-${badge.key}`} aria-label={badge.name}>
      <Icon className={ic} />
    </span>
  );
};

/** Earned + locked badges in a quiet strip (profile) or grid. */
export const BadgeStrip = ({ compact = false }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.badges().then(setData).catch(() => setData({ badges: [] })); }, []);
  if (!data) return <Skeleton className="h-12 w-full rounded-xl" />;
  const list = compact ? data.badges.filter((b) => b.earned) : data.badges;
  if (compact && list.length === 0) return null;
  return (
    <div data-testid="badge-strip">
      {!compact && <div className="flex items-end justify-between mb-3"><p className="eyebrow">Badges</p><p className="text-xs text-muted-foreground"><b className="text-foreground font-semibold">{data.earned_count}</b> of {data.total}</p></div>}
      <TooltipProvider delayDuration={0}>
        <div className={compact ? "flex flex-wrap gap-2" : "grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-3"}>
          {list.map((b) => (
            <Tooltip key={b.key}>
              <TooltipTrigger asChild>
                <div className={`flex ${compact ? "" : "flex-col items-center text-center gap-2"}`}>
                  <BadgeMedal badge={b} size={compact ? "sm" : "md"} />
                  {!compact && <p className={`text-[11px] leading-tight ${b.earned ? "font-medium" : "text-muted-foreground"}`}>{b.name}</p>}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px]"><p className="font-semibold">{b.name}</p><p className="text-xs text-muted-foreground">{b.description}{!b.earned ? " (locked)" : ""}</p></TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
};

/** Call once per app session: pops a toast for each newly earned badge. */
export const useBadgeUnlocks = () => {
  useEffect(() => {
    const key = "threadlist.badges.checked";
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.badges().then((d) => {
      (d.new || []).slice(0, 3).forEach((b, i) => setTimeout(() => toast(`Badge unlocked - ${b.name}`, { description: b.description, icon: <Award className="h-4 w-4 text-gold-deep" /> }), 600 + i * 900));
    }).catch(() => {});
  }, []);
};

const Delta = ({ now, prev }) => {
  const d = (now || 0) - (prev || 0);
  if (!d) return <span className="inline-flex items-center text-[11px] text-muted-foreground"><Minus className="h-3 w-3" /></span>;
  const up = d > 0;
  return <span className={`inline-flex items-center text-[11px] font-medium ${up ? "text-good" : "text-bad"}`}>{up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(d)}</span>;
};

/** "Your week" recap - Fitted-style weekly summary card. */
export const WeeklyRecap = () => {
  const [r, setR] = useState(null);
  useEffect(() => { api.weeklyRecap().then(setR).catch(() => setR({})); }, []);
  const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="weekly-recap">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Your week</p>
        {r?.week_start && <p className="text-[11px] text-muted-foreground">{fmt(r.week_start)} - {fmt(r.week_end)}</p>}
      </div>
      {!r ? <Skeleton className="h-28 w-full mt-3 rounded-xl" /> : (
        <>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[
              ["Pieces added", r.pieces_added, r.pieces_added_prev, "recap-added"],
              ["Fits worn", r.outfits_worn, r.outfits_worn_prev, "recap-worn"],
              ["Fit pics", r.fitpics, null, "recap-fitpics"],
              ["Reactions", r.reactions, null, "recap-reactions"],
              ["Styled by AI", r.outfits_styled, null, "recap-styled"],
              ["Offers in", r.offers_received, null, "recap-offers"],
            ].map(([l, v, pv, tid]) => (
              <div key={l} data-testid={tid}><p className="text-[11px] text-muted-foreground">{l}</p><p className="font-mono-num font-semibold text-lg leading-none mt-1 inline-flex items-baseline gap-1.5">{v ?? 0}{pv != null && <Delta now={v} prev={pv} />}</p></div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-gold" /> Threadworth</p>
              <p className="font-mono-num font-semibold leading-none mt-1">{money(r.worth)} <span className={`text-xs font-medium ${r.worth_change > 0 ? "text-good" : r.worth_change < 0 ? "text-bad" : "text-muted-foreground"}`}>{r.worth_change > 0 ? "+" : ""}{r.worth_change ? money(r.worth_change) : "no change"}</span></p>
            </div>
            {r.top_piece ? (
              <Link to="/inventory" className="flex items-center gap-2 min-w-0" data-testid="recap-top-piece">
                <div className="h-9 w-9 rounded-lg bg-gray-50 overflow-hidden shrink-0"><img src={itemImage(r.top_piece)} alt="" className="h-full w-full object-cover mix-blend-multiply" /></div>
                <div className="min-w-0"><p className="text-[11px] text-muted-foreground">Most worn</p><p className="text-xs font-medium truncate">{r.top_piece.name}</p></div>
              </Link>
            ) : <p className="text-xs text-muted-foreground text-right max-w-[160px]">Log what you wear and your most-worn piece shows up here.</p>}
          </div>
        </>
      )}
    </section>
  );
};
